import { z } from 'zod';

export const MAINTENANCE_ACTIVITY_PROTOCOL_VERSION = 1 as const;
export const MAINTENANCE_ACTIVITY_RENEW_SECONDS = 10;
export const MAINTENANCE_ACTIVITY_LEASE_SECONDS = 30;

export type MaintenanceActivityRequest = {
  protocolVersion: 1;
  action: 'reserve' | 'renew' | 'release';
  kind: 'player-session' | 'recording';
  ownerId: string;
  permitId?: string;
  fence?: number;
};

export type MaintenanceActivityPermit = {
  permitId: string;
  kind: 'player-session' | 'recording';
  ownerId: string;
  fence: number;
  nativeGeneration: number;
  ownerProcessId: number;
  ownerProcessCreatedUtc: string;
  bootId: string;
  renewByUtc: string;
};

export type MaintenanceActivityReply = {
  admitted: boolean;
  permit: MaintenanceActivityPermit | null;
  errorCode: string | null;
};

export type MaintenanceActivityIpcAction =
  | 'maintenanceActivityReserve'
  | 'maintenanceActivityRenew'
  | 'maintenanceActivityRelease';

export type MaintenanceActivityIpcRequest = {
  action: MaintenanceActivityIpcAction;
  request: MaintenanceActivityRequest;
};

const boundedId = z.string().min(1).max(128);
const nonnegativeSafeInteger = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const utcMilliseconds = z.string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)
  .refine((value) => !Number.isNaN(Date.parse(value)), 'Invalid UTC timestamp');
const kindSchema = z.enum(['player-session', 'recording']);

export const maintenanceActivityReserveSchema = z.object({
  protocolVersion: z.literal(MAINTENANCE_ACTIVITY_PROTOCOL_VERSION),
  action: z.literal('reserve'),
  kind: kindSchema,
  ownerId: boundedId,
}).strict();

const ownedActivitySchema = z.object({
  protocolVersion: z.literal(MAINTENANCE_ACTIVITY_PROTOCOL_VERSION),
  kind: kindSchema,
  ownerId: boundedId,
  permitId: boundedId,
  fence: nonnegativeSafeInteger,
});

export const maintenanceActivityRenewSchema = ownedActivitySchema.extend({
  action: z.literal('renew'),
}).strict();

export const maintenanceActivityReleaseSchema = ownedActivitySchema.extend({
  action: z.literal('release'),
}).strict();

export const maintenanceActivityRequestSchema = z.discriminatedUnion('action', [
  maintenanceActivityReserveSchema,
  maintenanceActivityRenewSchema,
  maintenanceActivityReleaseSchema,
]);

export const maintenanceActivityPermitSchema = z.object({
  permitId: boundedId,
  kind: kindSchema,
  ownerId: boundedId,
  fence: nonnegativeSafeInteger,
  nativeGeneration: nonnegativeSafeInteger,
  ownerProcessId: z.number().int().min(1).max(0xffff_ffff),
  ownerProcessCreatedUtc: utcMilliseconds,
  bootId: boundedId,
  renewByUtc: utcMilliseconds,
}).strict();

export const maintenanceActivityReplySchema = z.object({
  admitted: z.boolean(),
  permit: maintenanceActivityPermitSchema.nullable(),
  errorCode: boundedId.nullable(),
}).strict().superRefine((value, context) => {
  if (value.admitted !== (value.permit !== null)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['permit'],
      message: 'Admitted replies require a permit; rejected replies must not include one',
    });
  }
  if (value.admitted && value.errorCode !== null) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['errorCode'], message: 'Admitted reply cannot have an error' });
  }
  if (!value.admitted && value.errorCode === null) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['errorCode'], message: 'Rejected reply requires an error' });
  }
});

export function createMaintenanceActivityIpcRequest(
  request: MaintenanceActivityRequest,
): MaintenanceActivityIpcRequest {
  const parsed = maintenanceActivityRequestSchema.parse(request);
  const suffix = parsed.action[0].toUpperCase() + parsed.action.slice(1);
  return {
    action: `maintenanceActivity${suffix}` as MaintenanceActivityIpcAction,
    request: parsed as MaintenanceActivityRequest,
  };
}
